import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { toast } from "react-toastify";
import { createApiKey, getApiKeyByTenantId, revokeApiKey } from "./api/tenantApi";

const TenantApiKeyModal = ({ open, onClose, tenantId }) => {
  const [apiKey, setApiKey] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchApiKey = async () => {
    try {
      const response = await getApiKeyByTenantId(tenantId);
      if (response && response.data && response.data.key) {
        setApiKey(response.data.key);
        setExpiresAt(response.data.expiredAt ? response.data.expiredAt.split('T')[0] : "N/A");
        setStatus(response.data.active ? "ACTIVE" : "INACTIVE");
      } else {
        setApiKey("");
        setExpiresAt("");
        setStatus("");
      }
    } catch (error) {
      console.error("Failed to fetch API key:", error);
      // If no API key found, clear the state
      setApiKey("");
      setExpiresAt("");
      setStatus("");
    }
  };

  useEffect(() => {
    if (open && tenantId) {
      fetchApiKey();
    }
  }, [open, tenantId]);

  const handleCreateKey = async () => {
    setLoading(true);
    try {
      const response = await createApiKey(tenantId);
      if (response && response.data && response.data.key) {
        setApiKey(response.data.key);
        setExpiresAt(response.data.expiredAt ? response.data.expiredAt.split('T')[0] : "N/A");
        setStatus("ACTIVE");
        toast.success("API key created successfully!");
      }
    } catch (error) {
      console.error("Failed to create API key:", error);
      toast.error("Failed to create API key");
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async () => {
    setLoading(true);
    try {
      await revokeApiKey(tenantId);
      setStatus("REVOKED");
      toast.success("API key revoked successfully!");
    } catch (error) {
      console.error("Failed to revoke API key:", error);
      toast.error("Failed to revoke API key");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    toast.success("API key copied to clipboard!");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>API Key Management</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {apiKey ? (
            <>
              <TextField
                label='API Key'
                value={apiKey}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={handleCopyToClipboard}>
                      <ContentCopy />
                    </IconButton>
                  )
                }}
                fullWidth
              />
              <Typography variant='body2' color='text.secondary'>
                Expires At: {expiresAt || "N/A"}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Status: {status}
              </Typography>
            </>
          ) : (
            <Typography variant='body1' color='text.secondary'>
              No API key found.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='inherit'>
          Close
        </Button>
        {apiKey ? (
          <Button 
            onClick={handleRevokeKey} 
            color='error' 
            disabled={status === "REVOKED" || loading}
          >
            {loading ? "Revoking..." : "Revoke API Key"}
          </Button>
        ) : (
          <Button 
            onClick={handleCreateKey} 
            variant='contained'
            disabled={loading}
          >
            {loading ? "Creating..." : "Create API Key"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TenantApiKeyModal;
